import { Injectable } from '@angular/core';
import { NotificationService } from '@kimbocare/core';
import {
  applyTheme,
  argbFromHex,
  themeFromSourceColor,
} from '@material/material-color-utilities';
import { TranslateService } from '@ngx-translate/core';
import { LocalSessionService } from './local-session.service';
import { WhiteLabelService } from '../rest/white-label';
import { WhiteLabel } from '../models/white-label';
import { catchError, tap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  constructor(
    private whiteLabelService: WhiteLabelService,
    private localSessionService: LocalSessionService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
  ) {}

  init() {
    return this.getWhiteLabel();
  }

  getWhiteLabel() {
    return this.whiteLabelService.getWhiteLabel().pipe(
      // Store the label and generate the theme as side effects
      // but return the label as the observable value
      // so the caller can subscribe to it
      tap((label: WhiteLabel) => {
        this.localSessionService.storeWhiteLabel(label);
        this.generateDynamicTheme(label?.primary_color);
      }),
      catchError((err: any) => {
        this.notificationService.danger(
          this.translateService.instant(err.translate ?? 'errors.generic'),
        );
        // Rethrow or return EMPTY/throwError as needed
        return throwError(() => err);
      }),
    );
  }

  generateDynamicTheme(primary?: string) {
    const fallbackColor = '#005CBB';
    const sourceColor = primary ?? fallbackColor;
    let rgbColor;
    let argb;
    try {
      argb = argbFromHex(sourceColor);
      // Convert ARGB to usable RGB color
      const r = (argb >> 16) & 0xff;
      const g = (argb >> 8) & 0xff;
      const b = argb & 0xff;
      rgbColor = `${r} ${g} ${b}`;
      console.log('rgbColor', rgbColor);
      console.log('argb', argb);
    } catch (error) {
      // falling to default color if it's invalid color
      argb = argbFromHex(fallbackColor);
    }

    const targetElement = document.documentElement;

    // Get the theme from a hex color
    const theme = themeFromSourceColor(argb);

    // Print out the theme as JSON
    console.log(JSON.stringify(theme, null, 2));

    // Identify if user prefers dark theme
    const systemDark = window.matchMedia(
      '(prefers-color-scheme: dark)',
    ).matches;

    // Apply theme to root element
    applyTheme(theme, {
      target: targetElement,
      dark: false,
      brightnessSuffix: true,
    });

    const styles = targetElement.style;

    for (const key in styles) {
      if (Object.prototype.hasOwnProperty.call(styles, key)) {
        const propName = styles[key];
        if (propName.indexOf('--md-sys') === 0) {
          const sysPropName =
            '--mat-sys' + propName.replace('--md-sys-color', '');
          targetElement.style.setProperty(
            sysPropName,
            targetElement.style.getPropertyValue(propName),
          );
        }
      }
    }
    if (rgbColor) targetElement.style.setProperty('--mat-tw-primary', rgbColor);
    targetElement.style.setProperty('--mat-sys-primary', primary!);
  }
}
